import api, { fetchData } from './api';
import { CustomList, CreateListRequest, AddItemRequest, ListItem } from '../types/types';
import { logger } from './logger';

// Helper function to determine API prefix based on environment
// Matches the pattern from vendorApi.ts
const getApiPrefix = () => {
  const baseUrl = process.env.REACT_APP_API_URL || '';
  const endsWithApi = baseUrl.endsWith('/api');
  return endsWithApi ? '' : '/api';
};

/**
 * API functions for Custom Lists feature
 */

/**
 * Get all lists for the current user
 */
export const getUserLists = async (): Promise<CustomList[]> => {
  try {
    const apiPrefix = getApiPrefix();
    const response = await api.get(`${apiPrefix}/lists/`);
    return response.data;
  } catch (error) {
    logger.error('Error fetching user lists:', error);
    throw error;
  }
};

/**
 * Create a new list
 */
export const createList = async (listData: CreateListRequest): Promise<CustomList> => {
  try {
    const apiPrefix = getApiPrefix();
    const response = await api.post(`${apiPrefix}/lists/`, listData);
    logger.log('List created:', response.data);
    return response.data;
  } catch (error) {
    logger.error('Error creating list:', error);
    throw error;
  }
};

/**
 * Add an item to a list
 */
export const addItemToList = async (
  listId: number,
  itemData: AddItemRequest
): Promise<ListItem> => {
  try {
    const apiPrefix = getApiPrefix();
    const response = await api.post(`${apiPrefix}/lists/${listId}/items/`, itemData);
    logger.log('Item added to list:', response.data);
    return response.data;
  } catch (error) {
    logger.error('Error adding item to list:', error);
    throw error;
  }
};

/**
 * Remove an item from a list
 */
export const removeItemFromList = async (listId: number, itemId: number): Promise<void> => {
  try {
    const apiPrefix = getApiPrefix();
    await api.delete(`${apiPrefix}/lists/${listId}/items/${itemId}/`);
    logger.log('Item removed from list');
  } catch (error) {
    logger.error('Error removing item from list:', error);
    throw error;
  }
};

/**
 * Get a shared list by UUID (public, no auth required)
 */
export const getSharedList = async (uuid: string): Promise<CustomList> => {
  try {
    const apiPrefix = getApiPrefix();
    // Use fetchData for public endpoint (no auth)
    const response = await fetchData<CustomList>(`${apiPrefix}/share/list/${uuid}/`);
    return response;
  } catch (error) {
    logger.error('Error fetching shared list:', error);
    throw error;
  }
};

/**
 * Get a single list by ID (includes items in detail view)
 */
export const getList = async (listId: number): Promise<CustomList> => {
  try {
    const apiPrefix = getApiPrefix();
    const response = await api.get(`${apiPrefix}/lists/${listId}/`);
    return response.data;
  } catch (error) {
    logger.error('Error fetching list:', error);
    throw error;
  }
};

/**
 * Update a list (title/description)
 */
export const updateList = async (
  listId: number,
  updates: Partial<CreateListRequest> & { visibility?: 'private' | 'shared' | 'public' }
): Promise<CustomList> => {
  try {
    const apiPrefix = getApiPrefix();
    const response = await api.patch(`${apiPrefix}/lists/${listId}/`, updates);
    logger.log('List updated:', response.data);
    return response.data;
  } catch (error) {
    logger.error('Error updating list:', error);
    throw error;
  }
};

/**
 * Delete a list (soft delete)
 */
export const deleteList = async (listId: number): Promise<void> => {
  try {
    const apiPrefix = getApiPrefix();
    await api.delete(`${apiPrefix}/lists/${listId}/`);
    logger.log('List deleted');
  } catch (error) {
    logger.error('Error deleting list:', error);
    throw error;
  }
};
